import React from 'react';

import { IconCircleArrowLeft } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { ButtonWithIcon } from 'ui-kit/Buttons';

import { ProductType } from '__generated__/globalTypes';
import { TapesUploadedTableFile } from './components/__generated__/TapesUploadedTable';
import { StatusCell } from './components/TapesUploadedTable';
import TapeUploadStepper, { TapeUploadStep } from './TapeUploadStepper';

type Props = {
  tape: TapesUploadedTableFile;
  currentStep: TapeUploadStep;
  onStepClick: (step: TapeUploadStep) => void;
  /**
   * Live Tapestry stage for the status pill, kept in lockstep with the stepper
   * (the caller resolves the polled job stage over File.tapestry_stage). Falls
   * back to the file's own stage; ignored for non-Tapestry tapes.
   */
  tapestryStage?: string | null;
  /**
   * Optional run-level control rendered inline at the right of the stepper row
   * (e.g. "Cancel processing" while a Tapestry run is cancellable). Living in
   * the sticky header keeps it reachable as the body scrolls, instead of
   * taking a dedicated row above the body.
   */
  stepperAction?: React.ReactNode;
};

const HEADER_TOOLTIP_ID = 'tape-detail-filename-tooltip';

// -top-5/-mx-5 cancel the scroll container's p-5 (PortfolioLayout): sticky
// pins at the padding-box edge, so without the negative offsets the page
// content stays visible scrolling through the padding band above/beside
// the header. Full-bleed + negative top makes the header the true ceiling.
const HEADER_FRAME_CLASSES =
  'sticky -top-5 z-20 -mx-5 mb-4 border-b border-pink-500 bg-background-canvas px-9 pb-3 pt-4 shadow-lg';

const BackToTapesButton: React.FC = () => {
  const navigate = useNavigate();
  return (
    <ButtonWithIcon
      onClick={() => navigate('/portfolio/tape-upload')}
      iconPosition="left"
      icon={<IconCircleArrowLeft size="18px" />}
      label="Back to tapes"
      size="medium"
      type="link"
      className="border-none !px-0"
    />
  );
};

/**
 * First paint of the detail page while the tape query resolves: the same
 * sticky frame with the back button live immediately and pulse placeholders
 * where the identity/status/stepper go — a frame, not a blank void.
 */
export const TapeDetailHeaderSkeleton: React.FC = () => (
  <div data-testid="tape-detail-header-skeleton" className={HEADER_FRAME_CLASSES}>
    <BackToTapesButton />
    <div className="mt-1 flex items-center gap-4">
      <span className="h-6 w-72 animate-pulse rounded bg-slate-700" />
      <span className="h-5 w-20 animate-pulse rounded bg-slate-800" />
      <span className="h-5 w-24 animate-pulse rounded bg-slate-800" />
    </div>
    <div className="mt-4 h-7 w-[28rem] max-w-full animate-pulse rounded bg-slate-800" />
  </div>
);

/**
 * Sticky identity + wayfinding frame for the tape detail page: back navigation,
 * which tape you're working, its live status, and where you are in the flow.
 * Sticks to the top of the scroll container so the context never scrolls away
 * during mapping/review.
 */
const TapeDetailHeader: React.FC<Props> = ({
  tape,
  currentStep,
  onStepClick,
  tapestryStage,
  stepperAction,
}) => {
  return (
    <div data-testid="tape-detail-header" className={HEADER_FRAME_CLASSES}>
      <BackToTapesButton />
      <div className="mt-1 flex items-center gap-4">
        <span
          data-testid="tape-detail-filename"
          className="max-w-[420px] truncate text-lg font-medium text-white"
          data-tooltip-id={HEADER_TOOLTIP_ID}
          data-tooltip-content={tape.name ?? ''}
        >
          {tape.name}
        </span>
        <span className="rounded-md border border-slate-600 px-2 py-0.5 font-pt-mono text-xs text-slate-300">
          {tape.assetClass}
          {tape.productType === ProductType.ARM ? ` · ${tape.productType}` : ''}
        </span>
        <span data-testid="tape-detail-status" className="text-[12px]">
          <StatusCell
            state={tape.state}
            processingPercent={tape.processing_percent}
            persistingPercent={tape.persisting_percent}
            tapestryEnabled={tape.tapestry_enabled}
            tapestryStage={tapestryStage ?? tape.tapestry_stage}
          />
        </span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <TapeUploadStepper currentStep={currentStep} onStepClick={onStepClick} />
        {stepperAction}
      </div>
      <Tooltip id={HEADER_TOOLTIP_ID} className="z-[100] max-w-[400px]" place="bottom" />
    </div>
  );
};

export default TapeDetailHeader;
